<template>
  <div class="w-screen h-screen flex">
    <!-- Left Section (Logo) -->
    <div
      class="left-section w-1/2 flex flex-col items-center justify-center p-10"
    >
      <figure class="logo-container mb-8">
        <img src="@/assets/main-logo.png" alt="Logo" class="h-20" />
      </figure>
    </div>

    <!-- Right Section (Form) -->
    <div class="w-1/2 bg-white flex items-center justify-center">
      <div
        class="bg-white shadow-lg rounded-xl p-8 max-w-md w-full border border-gray-200"
      >
        <!-- Info Banner -->
        <div
          class="bg-yellow-50 text-yellow-800 border border-yellow-300 p-3 rounded-md text-sm mb-4"
        >
          The verification code is valid for 1 hour. You won’t be able to sign
          in until you set a new password.
        </div>

        <!-- Title -->
        <div class="text-xl font-semibold text-gray-800 mb-6 text-center">
          Set New Password
        </div>

        <!-- Email Input -->
        <div class="mb-4">
          <label class="block text-gray-700 text-sm font-medium mb-2"
            >Email</label
          >
          <input
            class="custom-input custom-focus"
            type="email"
            v-model="email"
            placeholder="Enter your email"
          />
          <div v-if="emailError" class="text-red-600 text-sm mt-1">{{ emailError }}</div>

        </div>

        <!-- Code Input -->
        <div class="mb-2">
          <label class="block text-gray-700 text-sm font-medium mb-2"
            >Verification Code</label
          >
          <input
            class="custom-input custom-focus"
            type="text"
            v-model="code"
            placeholder="Enter the code from email"
          />
        </div>

        <!-- Resend Code Button -->
        <div class="mb-4 text-right">
          <CustomTooltip
            v-if="isResending || resendCooldown < 60"
            :text="`You can resend the code in ${resendCooldown}s`"
            placement="top"
          >
            <span class="text-sm text-gray-400 cursor-not-allowed">
              Resend Code
            </span>
          </CustomTooltip>

          <button
            v-else
            @click="resendCode"
            class="text-sm register-text hover:underline focus:outline-none"
          >
            Resend Code
          </button>
        </div>

        <!-- Password Input -->
        <div class="mb-4 relative">
          <label class="block text-gray-700 text-sm font-medium mb-2"
            >New Password</label
          >
          <input
            :type="showPassword ? 'text' : 'password'"
            class="custom-input custom-focus pr-10"
            v-model="newPassword"
            placeholder="Enter your password"
            @keyup.enter="submit"
          />
          <!-- Eye Icon -->
          <button
            type="button"
            class="absolute right-3 top-10 text-gray-500 hover:text-gray-700"
            @click="togglePasswordVisibility"
          >
            <i :class="showPassword ? 'fas fa-eye-slash' : 'fas fa-eye'"></i>
          </button>
          <div v-if="passwordError" class="text-red-600 text-sm mt-1">{{ passwordError }}</div>
        </div>

        <!-- Error Message -->
        <div
          v-if="error"
          class="error-message flex items-center justify-between p-3 mb-4"
        >
          <div class="flex items-center">
            <i class="fas fa-exclamation-circle text-red-600 mr-2 mt-0.5"></i>
            <span class="text-red-600 font-medium">{{ error }}</span>
          </div>
          <button @click="error = ''" class="text-red-600 hover:text-red-800">
            <i class="fas fa-times"></i>
          </button>
        </div>

        <!-- Success Message -->
        <div
          v-if="success"
          class="text-green-600 text-sm font-medium mb-4 text-center"
        >
          Password has been reset. Redirecting to login...
        </div>

        <!-- Submit Button -->
        <button
          @click="submit"
          :disabled="!isValid || isLoading"
          class="custom-button w-full flex items-center justify-center"
        >
          <span v-if="!isLoading">Confirm New Password</span>
          <span v-else class="auth-loader"></span>
        </button>

        <!-- Back to Login -->
        <div class="mt-4 text-sm text-center">
          Already set your password?
          <router-link to="/login" class="register-text">
            Back to Login
          </router-link>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { confirmForgotPasswordApi, forgotPasswordApi } from "../service/auth";
import CustomTooltip from "@/utilities/CustomTooltip.vue";

export default {
  name: "ConfirmForgotPassword",
  data() {
    return {
      email: "",
      code: "",
      newPassword: "",
      error: "",
      emailError: "",
      passwordError: "",
      success: false,
      isLoading: false,
      showPassword: false,
      isResending: false,
      resendCooldown: 60,
      cooldownInterval: null,
    };
  },
  components: {
    CustomTooltip,
  },
  created() {
    this.email = this.$route.query.email || "";
  },
  computed: {
    isValid() {
      return (
        this.email &&
        this.code &&
        this.newPassword &&
        !this.emailError &&
        !this.passwordError
      );
    },
  },
  methods: {
    validateEmail() {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (this.email && !emailRegex.test(this.email)) {
        this.emailError = "Please enter a valid email address.";
      } else {
        this.emailError = "";
      }
    },
    validatePassword() {
      if (this.newPassword.length < 6) {
        this.passwordError = "Password must be at least 6 characters.";
      } else {
        this.passwordError = "";
      }
    },
    togglePasswordVisibility() {
      this.showPassword = !this.showPassword;
    },
    async submit() {
      this.error = "";
      this.success = false;

      this.validateEmail();
      this.validatePassword();

      if (!this.isValid) {
        this.error = "Please fix the errors before submitting.";
        return;
      }

      try {
        this.isLoading = true;
        await confirmForgotPasswordApi(this.email, this.code, this.newPassword);
        this.success = true;

        setTimeout(() => {
          this.$router.push("/login");
        }, 1500);
      } catch (err) {
        this.error = err.message || "Something went wrong.";
      } finally {
        this.isLoading = false;
      }
    },
    async resendCode() {
      this.error = "";
      this.validateEmail();

      if (this.emailError) {
        this.error = "Please enter a valid email address.";
        return;
      }

      try {
        this.isResending = true;
        await forgotPasswordApi(this.email);
        this.$notify({
          type: "success",
          text: "A new verification code has been sent to your email.",
        });

        // Start cooldown
        this.resendCooldown = 60;
        this.cooldownInterval = setInterval(() => {
          if (this.resendCooldown > 0) {
            this.resendCooldown--;
          } else {
            clearInterval(this.cooldownInterval);
          }
        }, 1000);
      } catch (err) {
        this.error = err.message || "Failed to resend code. Please try again.";
      } finally {
        this.isResending = false;
      }
    },
  },
  watch: {
    email() {
      this.validateEmail();
    },
    newPassword() {
      this.validatePassword();
    },
  },
  beforeUnmount() {
    if (this.cooldownInterval) {
      clearInterval(this.cooldownInterval);
    }
  },
};
</script>
